import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import MentalHealthCheckIn from '@/components/checkin/MentalHealthCheckIn';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Brain, Sparkles, TrendingUp, PieChart, MessageCircle, ArrowRight, Wrench, Heart, Scale } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function MentalWellnessPage() {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  useNavigationHistory();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader title="Mental Wellness" />

      <div className="container mx-auto p-4 max-w-4xl">
        {/* Welcome Card */}
        <Card className="p-6 mb-6 bg-gradient-to-br from-primary/20 via-accent/10 to-secondary/10 border-primary/30">
          <div className="flex items-start gap-4">
            <div className="p-4 rounded-full bg-gradient-to-br from-primary/30 to-accent/20">
              <Brain className="w-8 h-8 text-secondary" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold mb-2 text-foreground">Mental Wellness Hub</h1>
              <p className="text-foreground/80 mb-4">
                Your central space for mental health tracking, thought work, and self-discovery. 
                Everything here feeds into Therappi® for personalised support.
              </p>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                <div className="p-3 rounded-lg bg-background/60 border border-border/50">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-4 h-4 text-secondary" />
                    <span className="font-semibold text-sm text-foreground">Self-Awareness</span>
                  </div>
                  <p className="text-xs text-foreground/70">
                    Understand your patterns
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-background/60 border border-border/50">
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="w-4 h-4 text-accent" />
                    <span className="font-semibold text-sm text-foreground">AI Insights</span>
                  </div>
                  <p className="text-xs text-foreground/70">
                    Therappi® learns your needs
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-background/60 border border-border/50">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-accent" />
                    <span className="font-semibold text-sm text-foreground">Track Progress</span>
                  </div>
                  <p className="text-xs text-foreground/70">
                    See your growth over time
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Featured: Therapeutic Tools Hub */}
        <Card 
          className="mb-6 cursor-pointer hover:shadow-lg transition-all group bg-gradient-to-r from-accent/15 via-primary/10 to-secondary/10 border-accent/30"
          onClick={() => navigate('/mental-tools')}
        >
          <CardHeader>
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-gradient-to-br from-accent/25 to-primary/20 group-hover:scale-110 transition-transform">
                <Wrench className="w-6 h-6 text-secondary" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-xl flex items-center justify-between text-foreground">
                  Therapeutic Tools Hub
                  <ArrowRight className="w-5 h-5 text-foreground/60 group-hover:text-secondary group-hover:translate-x-1 transition-all" />
                </CardTitle>
                <CardDescription className="mt-1 text-foreground/70">
                  Evidence-based exercises: CBT, ACT, Schema Therapy, Self-Compassion, Behavioural Activation & more
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {['Emotion Wheel', 'Body Mapping', 'Guided Reframing', 'Values Explorer', 'Schema Work', 'Self-Compassion'].map((tool) => (
                <span key={tool} className="px-2 py-1 text-xs rounded-full bg-secondary/20 text-foreground">
                  {tool}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Navigation Cards */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card 
            className="cursor-pointer hover:border-primary/50 hover:shadow-md transition-all group"
            onClick={() => navigate('/thought-tracker')}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <PieChart className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg flex items-center justify-between">
                    Thought Tracker
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Log thoughts, identify cognitive distortions, and challenge unhelpful thinking patterns with CBT techniques
              </CardDescription>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-accent/50 hover:shadow-md transition-all group"
            onClick={() => navigate('/therapy', { state: { mode: 'cbt' } })}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-accent/10 group-hover:bg-accent/20 transition-colors">
                  <MessageCircle className="w-5 h-5 text-accent" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg flex items-center justify-between">
                    Therappi CBT Mode
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors" />
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Get AI-guided support to challenge negative thoughts and develop healthier thinking patterns
              </CardDescription>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-secondary/50 hover:shadow-md transition-all group"
            onClick={() => navigate('/sanctuary')}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-secondary/10 group-hover:bg-secondary/20 transition-colors">
                  <Sparkles className="w-5 h-5 text-secondary" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg flex items-center justify-between">
                    Sanctuary Tools
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-secondary transition-colors" />
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                Access coping tools, skills library, and condition-specific support resources
              </CardDescription>
            </CardContent>
          </Card>

          <Card 
            className="cursor-pointer hover:border-accent/50 hover:shadow-md transition-all group"
            onClick={() => navigate('/my-scores')}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-accent/10 group-hover:bg-accent/20 transition-colors">
                  <TrendingUp className="w-5 h-5 text-accent" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg flex items-center justify-between">
                    My Scores
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors" />
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <CardDescription>
                View your Mental Wellness Score, trends, and insights from your tracked data
              </CardDescription>
            </CardContent>
          </Card>
        </div>

        {/* Main Check-In Component */}
        <MentalHealthCheckIn />

        {/* Info Card */}
        <Card className="p-6 mt-6 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
          <h3 className="font-semibold mb-2">Why Evening Check-Ins?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Evening reflection captures your full day's experience when memories are fresh. 
            Research shows evening journalling improves sleep quality by 42% and emotional processing by 68%.
          </p>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => navigate('/my-scores')}
          >
            View My Scores Dashboard
          </Button>
        </Card>
      </div>
    </div>
  );
}